import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import SearchInput from "../../../utils/SearchInput";
import PaginationComponent from "../../../utils/PaginationComponent";
import TableLoader from "../../../utils/TableLoader";
import { FaPlus } from "react-icons/fa";
import { BiSolidTrashAlt } from "react-icons/bi";
import { TbEdit } from "react-icons/tb";
import { motion, AnimatePresence } from "framer-motion";
import {
  containerVariants,
  itemVariants,
  buttonAnimations,
  cardVariants
} from "../../../utils/Animations";
import RestUserEdit from "./RestUserEdit";

const RestaurantUser = ((props, ref) => {
  const { hasPermission } = props;
  const { rest_slug2 } = useParams();
  const navigate = useNavigate();

  const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
  const token = localStorage.getItem("token");

  const [isLoading, setIsLoading] = useState({ main: false, edit: false });
  const [users, setUsers] = useState([]);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const [clientName, setClientName] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mobile_phone, setMobilePhone] = useState("");
  const [selectedState, setSelectedState] = useState("Active");
  const [selectedRole, setSelectedRole] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [searchText, setSearchText] = useState("");

  const handleSearchChange = (value) => {
    setSearchText(value);
  };

  const getClientName = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/v1/restaurants/${rest_slug2}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = response.data;
      setClientName(data?.company_name);
    } catch (error) {
      console.error('Error fetching order data:', error);
    };
  }

  const getRestaurantUser = async () => {
    setIsLoading({ ...isLoading, main: true });
    try {
      let params;
      if (searchText) {
        params = {
          pageNumber: page,
          size: rowsPerPage,
          search: searchText,
        };
      } else {
        params = {
          pageNumber: page,
          size: rowsPerPage,
        };
      }
      const response = await axios.get(`${apiUrl}/api/v1/restaurants/${rest_slug2}/get-users`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response?.data;
      setUsers(data?.data);
      setTotalPages(data?.total);
    } catch (error) {
      console.error('Error fetching order data:', error);
    } finally {
      setIsLoading({ ...isLoading, main: false });
    }
  };

  const handleSave = async () => {
    setIsLoading({ ...isLoading, edit: true });
    try {
      const payload = {
        name,
        surname,
        email,
        password,
        mobile_phone,
        status: selectedState,
        role_id: selectedRole,
      };
      const response = await axios.put(
        `${apiUrl}/api/v1/update-restaurant-user/${currentUserData?.id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      getRestaurantUser();
      toast.success(response?.message || 'User Editing successfully!', {
        theme: 'colored',
        autoClose: 1000,
      });
    } catch (error) {
      console.error('Error Editing user:', error?.response?.data?.errors);
      const errors = error?.response?.data?.errors;
      if (errors) {
        for (const key in errors) {
          if (Array.isArray(errors[key])) {
            errors[key].forEach((message) => {
              toast.error(message, {
                theme: 'colored',
                autoClose: 3000,
              });
            });
          }
        }
      } else {
        toast.error('Something went wrong. Please try again.', {
          theme: 'colored',
          autoClose: 3000,
        });
      }
    } finally {
      setIsLoading({ ...isLoading, edit: false });
      setIsEditing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!id) {
      console.error('Error: ID is required to delete a user.');
      return;
    }
    setIsLoading({ ...isLoading, main: true });
    try {
      const response = await axios.delete(`${apiUrl}/api/v1/users/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      toast.error('User deleted successfully:', { theme: "colored", autoClose: 1000 }, response?.data?.message || response?.status);
      getRestaurantUser();
    } catch (error) {
      console.error('Error deleting user:', error?.response?.data?.message || error.message);
      toast.error('Failed to delete the user. Please try again.', { theme: 'colored', autoClose: 3000 });
    } finally {
      setIsLoading({ ...isLoading, main: false });
    }
  };

  const handleEditClick = (user) => {
    setIsEditing(true);
    setCurrentUserData(user);
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setCurrentUserData(null);
  };

  useImperativeHandle(ref, () => ({
    restUser() {
      setIsEditing(false);
      setCurrentUserData(null);
    },
  }));

  useEffect(() => {
    if (currentUserData) {
      setName(currentUserData?.name || "");
      setSurname(currentUserData?.surname || "")
      setEmail(currentUserData?.email || "")
      setPassword(currentUserData?.password || "")
      setMobilePhone(currentUserData?.mobile_phone || "")
      setSelectedState(currentUserData?.status || "")
      setSelectedRole(currentUserData?.role_id || "")
    }
  }, [currentUserData]);

  useEffect(() => {
    getRestaurantUser();
    getClientName();
  }, [page, rowsPerPage, searchText])

  useEffect(() => {
    const handlePageRefresh = () => {
      localStorage.removeItem("searchTextUser");
    };
    window.addEventListener("beforeunload", handlePageRefresh);
    return () => {
      window.removeEventListener("beforeunload", handlePageRefresh);
    };
  }, [])

  return (
    <AnimatePresence mode="wait">
      {isEditing ? (
        <motion.div
          key="edit-category"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          <RestUserEdit
            isLoading={isLoading}
            name={name}
            surname={surname}
            email={email}
            password={password}
            mobile_phone={mobile_phone}
            selectedState={selectedState}
            selectedRole={selectedRole}
            setName={setName}
            setSurname={setSurname}
            setEmail={setEmail}
            setPassword={setPassword}
            setMobilePhone={setMobilePhone}
            setSelectedState={setSelectedState}
            setSelectedRole={setSelectedRole}
            handleSave={handleSave}
            handleCancel={handleCancel}
          />
        </motion.div>
      ) : (
        <motion.div
          key="user-list"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          <motion.section
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="border-t border-[#D9D9D9] sm:mt-5 sm:pt-10 pt-5"
          >
            <motion.div
              className="flex lg:flex-row flex-col justify-between items-center mt-3 mb-3 w-full"
              variants={containerVariants}
            >
              <motion.h3
                className="text-3xl font-bold mb-2 poppins-medium self-start"
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.2 }}
              >
                Utenti di {clientName || "N/A"}
              </motion.h3>
              <motion.div
                className="self-start flex items-center gap-4 sm:flex-row flex-col sm:w-auto w-full"
                variants={itemVariants}
              >
                <SearchInput
                  onSearchChange={handleSearchChange}
                  component="Utenti Restaurant"
                  placeholder="Utenti"
                />
                {hasPermission("Users", "add") && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="flex sm:w-auto w-full items-center justify-center px-2 py-1 bg-[#8A2533] text-white rounded-lg border-2 border-[#953543] shadow-sm hover:bg-[#8A2533] transition-all duration-200 group"
                    onClick={() => navigate(`/restaurantPanel/restaurant-user-add/${localStorage.getItem("currentRestId")}`)}
                    aria-label="Aggiungi nuovo utente"
                  >
                    <FaPlus />
                    <span className="ml-2 poppins-medium">Aggiungi</span>
                  </motion.button>
                )}
              </motion.div>
            </motion.div>
            <motion.div
              className="user-mangement-main w-auto h-auto bg-[#FBFBFB] border-[2px] border-[#8B24332B] px-6 pt-3 pb-0 rounded-xl shadow"
              variants={cardVariants}
            >
              <table className="w-full text-left wine_table">
                <thead>
                  <motion.tr
                    className="border-b border-[#8B2433]"
                    variants={itemVariants}
                  >
                    <th className="py-4 px-4 text-left font-semibold poppins-medium text-[#6B6B6B]">Nome</th>
                    <th className="py-4 px-4 text-left font-semibold poppins-medium text-[#6B6B6B]">Cognome</th>
                    <th className="py-4 px-4 text-left font-semibold poppins-medium text-[#6B6B6B]">Cellulare</th>
                    <th className="py-4 px-4 text-left font-semibold poppins-medium text-[#6B6B6B]">Email</th>
                    <th className="py-4 px-4 text-left font-semibold poppins-medium text-[#6B6B6B]">Stato</th>
                    {(hasPermission("Users", "edit") || hasPermission("Users", "remove")) && (
                      <th className="py-4 px-4 text-left font-semibold poppins-medium text-[#6B6B6B]">Opzioni</th>
                    )}
                  </motion.tr>
                </thead>
                {isLoading?.main ? (
                  <TableLoader />
                ) : (
                  <tbody>
                    {users?.length ? (
                      users?.map((user, index) => (
                        <motion.tr
                          key={index}
                          className="border-b border-[#8B2433]/12"
                          variants={itemVariants}
                          custom={index}
                        >
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">{user?.name?.replace(/\s+/g, ' ') || "N/A"}</td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">{user?.surname?.replace(/\s+/g, ' ') || "N/A"}</td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">{user?.mobile_phone?.trim() || "N/A"}</td>
                          <td className="py-4 px-2 text-left poppins-medium text-[#303030]">{user?.email || "N/A"}</td>
                          <td className="py-4 px-2 text-left">
                            <span
                              className={`inline-flex items-center justify-center w-20 py-1 px-3 rounded-full text-center font-extrabold transition-colors ${user?.status === "Active"
                                ? "text-[#0AC83D] active_lable"
                                : "text-[#EB2D2D] inactive_lable"
                                }`}
                            >
                              {user?.status === "Active" ? "Attivo" : user?.status === "Inactive" ? "Inattivo" : "N/A"}
                            </span>
                          </td>
                          {(hasPermission("Users", "edit") || hasPermission("Users", "remove")) && (
                            <td className="py-4 px-4 flex items-center space-x-2 h-[100px] w-[100px]">
                              {hasPermission("Users", "edit") && (
                                <motion.button
                                  className="text-red-700"
                                  onClick={() => handleEditClick(user)}
                                  aria-label={`Modifica utente ${user?.name}`}
                                  {...buttonAnimations}
                                >
                                  <TbEdit size={20} />
                                </motion.button>
                              )}
                              {hasPermission("Users", "remove") && (
                                <motion.button
                                  className="text-red-700"
                                  onClick={() => handleDelete(user?.id)}
                                  aria-label={`Elimina utente ${user?.name}`}
                                  {...buttonAnimations}
                                >
                                  <BiSolidTrashAlt size={18} />
                                </motion.button>
                              )}
                            </td>
                          )}
                        </motion.tr>
                      ))
                    ) : (
                      <motion.tr variants={itemVariants}>
                        <td colSpan={3} className="text-center py-4">
                          Nessun utente trovato
                        </td>
                      </motion.tr>
                    )}
                  </tbody>
                )}
              </table>
            </motion.div>
            {users && users?.length > 0 && (
              <PaginationComponent
                page={page}
                totalPages={totalPages}
                rowsPerPage={rowsPerPage}
                setPage={setPage}
                setRowsPerPage={setRowsPerPage}
                className="mt-6"
              />
            )}
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

export default forwardRef(RestaurantUser);
